import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../AxiosInstance";
import SidebarLayout from "../SidebarLayout";
import { useNotification } from "../NotificationContext";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const ManagementMain = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [userRole, setUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    password: "",
    confirmPassword: "",
    icpiename: "",
    email: "",
    role: "ROLE_ICPIE",
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editFormData, setEditFormData] = useState({
    email: "",
    role: "ROLE_ICPIE",
    slackId: "",
    password: "",
    confirmPassword: "",
    showPassword: false,
    showConfirmPassword: false,
  });
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }
    try {
      const decodedToken = jwtDecode(token);
      const role = decodedToken?.claims?.role;
      const id = decodedToken?.claims?.id ?? null;
      setUserRole(role);
      setCurrentUserId(id);
      if (role !== "ROLE_ICPIE") {
        navigate("/dashboard");
      }
    } catch {
      navigate("/");
    }
  }, [navigate]);

  const fetchEntries = () => {
    setLoading(true);
    axiosInstance
      .get("/icpie/all")
      .then((res) => {
        const data = res.data;
        if (data?.code === 0 && Array.isArray(data?.data)) {
          setEntries(data.data);
        } else if (Array.isArray(data)) {
          setEntries(data);
        } else if (data?.data && Array.isArray(data.data)) {
          setEntries(data.data);
        } else {
          setEntries([]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch entries:", err);
        setEntries([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (userRole === "ROLE_ICPIE") {
      fetchEntries();
    }
  }, [userRole]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "password" || name === "confirmPassword") {
      setPasswordMismatch(false);
    }
    setErrorMessage("");
  };

  const validateForm = () => {
    const { firstname, lastname, password, confirmPassword, icpiename, email } =
      formData;
    if (!firstname?.trim()) {
      setErrorMessage("First name is required");
      return false;
    }
    if (!lastname?.trim()) {
      setErrorMessage("Last name is required");
      return false;
    }
    if (!icpiename?.trim()) {
      setErrorMessage("Icpie name is required");
      return false;
    }
    if (!email?.trim()) {
      setErrorMessage("Email is required");
      return false;
    }
    if (!password?.trim()) {
      setErrorMessage("Password is required");
      return false;
    }
    if (password !== confirmPassword) {
      setPasswordMismatch(true);
      setErrorMessage("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setErrorMessage("");
    setPasswordMismatch(false);

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        firstname: formData.firstname.trim(),
        lastname: formData.lastname.trim(),
        password: formData.password,
        icpiename: formData.icpiename.trim(),
        email: formData.email.trim(),
        role: formData.role,
      };

      const response = await axiosInstance.post("/icpie/register", payload, {
        headers: { "Content-Type": "application/json" },
      });

      const result = response.data;
      if (result.code === 0) {
        showNotification("Account created successfully", "success");
        setDialogOpen(false);
        setFormData({
          firstname: "",
          lastname: "",
          password: "",
          confirmPassword: "",
          icpiename: "",
          email: "",
          role: "ROLE_ICPIE",
        });
        fetchEntries();
      } else {
        setErrorMessage(result.message || "Registration failed");
      }
    } catch (error) {
      const msg =
        error.response?.data?.message || error.message || "Registration failed";
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDialog = () => {
    setErrorMessage("");
    setPasswordMismatch(false);
    setFormData({
      firstname: "",
      lastname: "",
      password: "",
      confirmPassword: "",
      icpiename: "",
      email: "",
      role: "ROLE_ICPIE",
    });
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (row) => {
    setErrorMessage("");
    setEditingEntry(row);
    setEditFormData({
      email: row?.email || "",
      role: row?.role || "ROLE_ICPIE",
      slackId: row?.slackId ?? row?.slack_id ?? "",
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingEntry(null);
    setErrorMessage("");
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMessage("");
  };

  const handleUpdate = async () => {
    if (!editingEntry?.id) return;
    if (!editFormData.email?.trim()) {
      setErrorMessage("Email is required");
      return;
    }
    const password = editFormData.password || "";
    const confirmPassword = editFormData.confirmPassword || "";
    const isPasswordChange = password.trim() !== "" || confirmPassword.trim() !== "";
    if (isPasswordChange && password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...editingEntry,
        email: editFormData.email.trim(),
        role: editFormData.role,
        slackId: editFormData.slackId?.trim() || "",
      };
      if (isPasswordChange) {
        payload.password = password;
      }
      const response = await axiosInstance.put("/icpie/update", payload, {
        headers: { "Content-Type": "application/json" },
      });
      if (response?.data?.code === 0 || response?.data?.code === undefined) {
        showNotification("Account updated successfully", "success");
        setEditDialogOpen(false);
        fetchEntries();
        if (isPasswordChange && String(editingEntry.id) === String(currentUserId)) {
          localStorage.removeItem("token");
          localStorage.removeItem("role");
          showNotification("Password updated. Please log in again.", "info");
          navigate("/");
        }
      } else {
        setErrorMessage(response?.data?.message || "Update failed");
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || error.message || "Update failed",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editingEntry?.id) return;
    if (String(editingEntry.id) === String(currentUserId)) return;

    const ok = window.confirm(
      "Are you sure you want to delete this account? This action cannot be undone.",
    );
    if (!ok) return;

    setIsSubmitting(true);
    try {
      const response = await axiosInstance.delete("/icpie/delete", {
        params: { icpieId: editingEntry.id },
      });
      if (response?.data?.code === 0 || response?.data?.code === undefined) {
        showNotification("Account deleted successfully", "success");
        setEditDialogOpen(false);
        fetchEntries();
      } else {
        setErrorMessage(response?.data?.message || "Delete failed");
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || error.message || "Delete failed",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setErrorMessage("");
  };

  if (userRole !== "ROLE_ICPIE") {
    return null;
  }

  return (
    <SidebarLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" component="h1">
              Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenDialog}
            >
              Register New Account
            </Button>
          </Box>

          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Icpie Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Slack ID</TableCell>
                  <TableCell>Edit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No entries yet
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.firstname || "-"}</TableCell>
                      <TableCell>{row.lastname || "-"}</TableCell>
                      <TableCell>{row.icpiename || "-"}</TableCell>
                      <TableCell>{row.email || "-"}</TableCell>
                      <TableCell>{row.role || "-"}</TableCell>
                      <TableCell>{row.slackId ?? row.slack_id ?? ""}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenEditDialog(row)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Register New Account</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="First Name"
                  name="firstname"
                  value={formData.firstname}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Last Name"
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Icpie Name"
                  name="icpiename"
                  value={formData.icpiename}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={passwordMismatch}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={passwordMismatch}
                  helperText={passwordMismatch ? "Passwords do not match" : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    label="Role"
                    onChange={handleChange}
                  >
                    <MenuItem value="ROLE_ICPIE">ROLE_ICPIE</MenuItem>
                    <MenuItem value="ROLE_ICPIS">ROLE_ICPIS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Register"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={editFormData.email}
                  onChange={handleEditChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={editFormData.role}
                    label="Role"
                    onChange={handleEditChange}
                  >
                    <MenuItem value="ROLE_ICPIE">ROLE_ICPIE</MenuItem>
                    <MenuItem value="ROLE_ICPIS">ROLE_ICPIS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Slack ID"
                  name="slackId"
                  value={editFormData.slackId}
                  onChange={handleEditChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  name="password"
                  type={editFormData.showPassword ? "text" : "password"}
                  value={editFormData.password}
                  onChange={handleEditChange}
                  helperText="Leave blank to keep existing password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() =>
                            setEditFormData((prev) => ({
                              ...prev,
                              showPassword: !prev.showPassword,
                            }))
                          }
                        >
                          {editFormData.showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirmPassword"
                  type={editFormData.showConfirmPassword ? "text" : "password"}
                  value={editFormData.confirmPassword}
                  onChange={handleEditChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() =>
                            setEditFormData((prev) => ({
                              ...prev,
                              showConfirmPassword: !prev.showConfirmPassword,
                            }))
                          }
                        >
                          {editFormData.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            color="error"
            onClick={handleDelete}
            disabled={
              isSubmitting ||
              !editingEntry?.id ||
              String(editingEntry?.id) === String(currentUserId)
            }
          >
            Delete
          </Button>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={isSubmitting}>
            {isSubmitting ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
};

export default ManagementMain;
